import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy, limit,
  onSnapshot, addDoc, deleteDoc, doc, getDocs,
  updateDoc, setDoc, arrayUnion, arrayRemove,
  increment, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// useCookbooks  — my cookbooks (owner)
// ---------------------------------------------------------------------------
export const useCookbooks = () => {
  const { user } = useAuth();
  const [cookbooks, setCookbooks] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    if (!user) {
      setCookbooks([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'recipeCollections'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        setCookbooks(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching cookbooks:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ── Create ────────────────────────────────────────────────────────────────

  const createCookbook = async (
    name,
    description    = '',
    isPublic       = false,
    careProfileIds = [],
    coverImageURL  = null,
    status         = 'private',
    dietaryTags    = [],
    sections       = [],
  ) => {
    if (!user) { alert('Please sign in to create cookbooks'); return; }
    try {
      const resolvedStatus = status || (isPublic ? 'published' : 'private');
      const docRef = await addDoc(collection(db, 'recipeCollections'), {
        userId:         user.uid,
        ownerName:      user.displayName || user.email?.split('@')[0] || 'Nutrius User',
        name,
        description,
        status:         resolvedStatus,
        isPublic:       resolvedStatus === 'published',
        recipeIds:      [],
        careProfileIds: careProfileIds || [],
        coverImageURL:  coverImageURL  || null,
        followCount:    0,
        dietaryTags:    dietaryTags    || [],
        sections:       sections       || [],
        createdAt:      new Date(),
        updatedAt:      new Date(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating cookbook:', error);
      throw error;
    }
  };

  // ── Update metadata ───────────────────────────────────────────────────────

  const updateCookbook = async (cookbookId, { name, description, status, dietaryTags, careProfileIds }) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'recipeCollections', cookbookId), {
        name,
        description,
        status,
        isPublic:       status === 'published',
        dietaryTags:    dietaryTags    || [],
        careProfileIds: careProfileIds || [],
        updatedAt:      new Date(),
      });
    } catch (error) {
      console.error('Error updating cookbook:', error);
      throw error;
    }
  };

  // ── Update sections (blog body) ───────────────────────────────────────────

  const updateSections = async (cookbookId, sections) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'recipeCollections', cookbookId), {
        sections:  sections || [],
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating sections:', error);
      throw error;
    }
  };

  // ── Update cover image ────────────────────────────────────────────────────

  const updateCoverImage = async (cookbookId, coverImageURL) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'recipeCollections', cookbookId), {
        coverImageURL,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating cover image:', error);
      throw error;
    }
  };

  // ── Recipe membership ─────────────────────────────────────────────────────

  const addRecipeToCookbook = async (cookbookId, firestoreId) => {
    if (!user) { alert('Please sign in to add recipes to cookbooks'); return; }
    try {
      await updateDoc(doc(db, 'recipeCollections', cookbookId), {
        recipeIds: arrayUnion(firestoreId),
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error adding recipe to cookbook:', error);
      throw error;
    }
  };

  const removeRecipeFromCookbook = async (cookbookId, firestoreId) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'recipeCollections', cookbookId), {
        recipeIds: arrayRemove(firestoreId),
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error removing recipe from cookbook:', error);
      throw error;
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const deleteCookbook = async (cookbookId) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'recipeCollections', cookbookId));
    } catch (error) {
      console.error('Error deleting cookbook:', error);
      throw error;
    }
  };

  const isRecipeInCookbook = (cookbookId, firestoreId) => {
    const cookbook = cookbooks.find(cb => cb.id === cookbookId);
    return cookbook?.recipeIds?.includes(firestoreId) || false;
  };

  return {
    cookbooks,
    loading,
    createCookbook,
    updateCookbook,
    updateSections,
    updateCoverImage,
    addRecipeToCookbook,
    removeRecipeFromCookbook,
    deleteCookbook,
    isRecipeInCookbook,
  };
};

// ---------------------------------------------------------------------------
// usePublicCookbooks
// ---------------------------------------------------------------------------
export const usePublicCookbooks = ({ dietaryTag = null } = {}) => {
  const [all,     setAll]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'recipeCollections'),
      where('status', '==', 'published'),
      orderBy('followCount', 'desc'),
      orderBy('createdAt',   'desc'),
      limit(100)
    );

    const unsub = onSnapshot(q,
      snap => {
        setAll(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      err => {
        console.error('usePublicCookbooks:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const cookbooks = dietaryTag
    ? all.filter(cb => (cb.dietaryTags || []).includes(dietaryTag))
    : all;

  return { cookbooks, all, loading };
};

// ---------------------------------------------------------------------------
// useCookbookFollow
// ---------------------------------------------------------------------------
export const useCookbookFollow = (cookbookId, userId) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!cookbookId || !userId) { setLoading(false); return; }

    const unsub = onSnapshot(
      doc(db, 'cookbookFollows', `${userId}_${cookbookId}`),
      snap => { setIsFollowing(snap.exists()); setLoading(false); }
    );

    return () => unsub();
  }, [cookbookId, userId]);

  const toggle = useCallback(async () => {
    if (!userId || !cookbookId) return;

    const followRef   = doc(db, 'cookbookFollows',   `${userId}_${cookbookId}`);
    const cookbookRef = doc(db, 'recipeCollections', cookbookId);

    if (isFollowing) {
      await deleteDoc(followRef);
      await updateDoc(cookbookRef, { followCount: increment(-1) }).catch(() => {});
    } else {
      await setDoc(followRef, {
        userId,
        cookbookId,
        followedAt: serverTimestamp(),
      });
      await updateDoc(cookbookRef, { followCount: increment(1) }).catch(() => {});
    }
  }, [cookbookId, userId, isFollowing]);

  return { isFollowing, loading, toggle };
};

// ---------------------------------------------------------------------------
// useCookbookDetail
// ---------------------------------------------------------------------------
export const useCookbookDetail = (cookbookId) => {
  const [cookbook,       setCookbook]       = useState(null);
  const [recipes,        setRecipes]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [error,          setError]          = useState(null);

  useEffect(() => {
    if (!cookbookId) { setLoading(false); return; }

    const unsub = onSnapshot(
      doc(db, 'recipeCollections', cookbookId),
      snap => {
        if (!snap.exists()) {
          setError('Cookbook not found');
          setCookbook(null);
        } else {
          setCookbook({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      err => {
        console.error('useCookbookDetail:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [cookbookId]);

  const recipeIdsKey = cookbook?.recipeIds?.join(',') || '';

  useEffect(() => {
    if (!cookbook) return;
    const ids = cookbook.recipeIds || [];
    if (!ids.length) { setRecipes([]); return; }

    const fetch = async () => {
      setLoadingRecipes(true);
      try {
        const batches = [];
        for (let i = 0; i < ids.length; i += 10) batches.push(ids.slice(i, i + 10));
        const loaded = [];
        for (const batch of batches) {
          const snap = await getDocs(
            query(collection(db, 'recipes'), where('__name__', 'in', batch))
          );
          snap.docs.forEach(d => loaded.push({ ...d.data(), firestoreId: d.id }));
        }
        loaded.sort((a, b) => ids.indexOf(a.firestoreId) - ids.indexOf(b.firestoreId));
        setRecipes(loaded.filter(r => r.status === 'approved' && r.isVisible !== false));
      } catch (err) {
        console.error('useCookbookDetail recipes:', err);
      } finally {
        setLoadingRecipes(false);
      }
    };

    fetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeIdsKey]);

  return { cookbook, recipes, loading, loadingRecipes, error };
};

// ---------------------------------------------------------------------------
// useFollowedCookbooks
// ---------------------------------------------------------------------------
export const useFollowedCookbooks = (userId) => {
  const [followedIds, setFollowedIds] = useState([]);
  const [cookbooks,   setCookbooks]   = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    const unsub = onSnapshot(
      query(collection(db, 'cookbookFollows'), where('userId', '==', userId)),
      snap => setFollowedIds(snap.docs.map(d => d.data().cookbookId))
    );

    return () => unsub();
  }, [userId]);

  const followedKey = followedIds.join(',');

  useEffect(() => {
    if (!followedIds.length) { setCookbooks([]); setLoading(false); return; }

    const fetch = async () => {
      try {
        const batches = [];
        for (let i = 0; i < followedIds.length; i += 10) {
          batches.push(followedIds.slice(i, i + 10));
        }
        const loaded = [];
        for (const batch of batches) {
          const snap = await getDocs(
            query(collection(db, 'recipeCollections'), where('__name__', 'in', batch))
          );
          snap.docs.forEach(d => loaded.push({ id: d.id, ...d.data() }));
        }
        setCookbooks(loaded);
      } catch (err) {
        console.error('useFollowedCookbooks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedKey]);

  return { cookbooks, loading };
};